import { createRouter as createVueRouter, createWebHistory } from 'vue-router';
import { useMainStore } from './store/index';
import PublicHomeView from '@/views/PublicHomeView';
import PublicConfiguratorView from '@/views/PublicConfiguratorView';
import PublicActiveOffersView from '@/views/PublicActiveOffersView';
import { checkPublicFeatureToggle, consumeMagicLink } from './services/digitalSalesService';

const DEFAULT_PAGE_TITLE = typeof document !== 'undefined'
  ? document.title
  : 'Digital Sales - Tailor your truck online';

const getPageTitle = (route, store) => {
  const marketId = typeof route.params.market === 'string'
    ? route.params.market.toLowerCase()
    : null;
  const market = store.markets.find(({ marketId: id }) => id === marketId);
  const marketPageTitle = market?.pageTitle?.trim();

  if (marketPageTitle) return marketPageTitle;

  return DEFAULT_PAGE_TITLE;
};

const loadPublicInitialData = async (to) => {
  const store = useMainStore();
  if (!store.market) store.market = to.params.market;
  store.initialDataLoaded = true;
  store.appLoading = true;

  store.locale = store.getDefaultLocale;

  let initialCalls = [
    store.loadTranslations().catch(err => console.error('loadTranslations-error', err)),
    checkPublicFeatureToggle('webapp_yearEndOfferBanner').catch(err => console.error('error fetching feature toggle :: ', err)),
    checkPublicFeatureToggle('webapp_year2024OfferBanner').catch(err => console.error('error fetching feature toggle :: ', err)),
    checkPublicFeatureToggle('webapp_show_ecolutionBlob').catch(err => console.error('error fetching feature toggle :: ', err)),
    checkPublicFeatureToggle('webapp_show_availableOffersPage_DW5_background').catch(err => console.error('error fetching feature toggle :: ', err)),
    checkPublicFeatureToggle('webapp_show_availableOffersPage_personalShopperCard').catch(err => console.error('error fetching feature toggle :: ', err)),
  ];

  if (to.query.ref) {
    initialCalls.push(
      consumeMagicLink(to.query.ref).catch(err => console.error('consumeMagicLink-error', err)),
    );
  }

  const initialLoad = await Promise.all(initialCalls);

  store.featureToggleData[`${store.market}#webapp_yearEndOfferBanner`] = initialLoad[1]?.data;
  store.featureToggleData[`${store.market}#webapp_year2024OfferBanner`] = initialLoad[2]?.data;
  store.featureToggleData[`${store.market}#webapp_show_ecolutionBlob`] = initialLoad[3]?.data;
  store.featureToggleData[`${store.market}#webapp_show_availableOffersPage_DW5_background`] = initialLoad[4]?.data;
  store.featureToggleData[`${store.market}#webapp_show_availableOffersPage_personalShopperCard`] = initialLoad[5]?.data;

  if (to.query.ref) {
    if (!initialLoad[5]?.data) {
      store.appLoading = false;
      store.offerId = null;
      store.addErrorToast('offerExpired', { translate: true });
      return ({ name: 'PublicActiveOffersView', params: { market: store.market } });
    }
  }

  store.appLoading = false;
};

const handlePublicOfferAccess = async (app, to) => {
  const id = to.query.id;
  if (!id) return ({
    name: 'PublicActiveOffersView',
    params: { market: to.params.market },
  });
  const store = useMainStore();
  store.market = to.params.market;
  if (!store.initialDataLoaded) {
    const initialDataResult = await loadPublicInitialData(to);
    if (initialDataResult) return initialDataResult;
  }
  const isNewOffer = store.offerId !== id;
  if (isNewOffer || !store.options) {
    store.appLoading = true;
    store.setOfferId(id);
    await store.loadConfiguration(to.query.ref);
    if(store.offerLoadDataError){
      store.appLoading = false;
      store.offerId = null;
      store.addErrorToast('errorLoadingOffer', { translate: true });
      return ({ name: 'PublicActiveOffersView', params: { market: store.market }, query: { ref: to.query.ref } });
    }
    if(store.offerStatus.expired) {
      store.appLoading = false;
      store.offerId = null;
      store.addErrorToast('offerExpired', { translate: true });
      return ({ name: 'PublicActiveOffersView', params: { market: store.market }, query: { ref: to.query.ref } });
    }
    if(store.offerStatus.isOfferNotValid) {
      store.appLoading = false;
      store.offerId = null;
      store.addErrorToast('offerNotValid', { translate: true });
      return ({ name: 'PublicActiveOffersView', params: { market: store.market }, query: { ref: to.query.ref } });
    }
    store.appLoading = false;
  }
};

const createRouter = (app) => {
  const router = createVueRouter({
    history: createWebHistory(),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      } else {
        return { top: 0 };
      }
    },
    routes: [
      {
        path: '/market',
        name: 'PublicHomeView',
        component: PublicHomeView,
        beforeEnter: async (to, from) => {
          return {
            name: 'PublicActiveOffersView',
            params: { market: 'pl' },
          };
        },
      },
      {
        path: '/market/:market',
        name: 'PublicHomeView',
        component: PublicHomeView,
        beforeEnter: async (to, from) => {
          return {
            name: 'PublicActiveOffersView',
            params: { market: to.params.market },
          };
        },
      },
      {
        path: '/market/:market/active',
        name: 'PublicActiveOffersView',
        component: PublicActiveOffersView,
        beforeEnter: async (to, from) => {
          const store = useMainStore();
          store.market = to.params.market;
          if (!store.initialDataLoaded) {
            const initialDataResult = await loadPublicInitialData(to);
            if (initialDataResult) return initialDataResult;
          }
        },
      },
      {
        path: '/market/:market/configure',
        name: 'PublicConfiguratorView',
        component: PublicConfiguratorView,
        beforeEnter: (to, from) => {
          return handlePublicOfferAccess(app, to);
        },
      },
    ],
  });

  router.afterEach((to) => {
    const store = useMainStore();
    document.title = getPageTitle(to, store);
  });

  return router;
};

export {
  createRouter,
};
